/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppAssetsWithSrc } from "Theme/EVENTS_ASSETS";
import { Button, FontAwesomeIcon, Input, Stack, Text } from "components";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/FormV2/FormV2";
import useEventRouting from "hooks/useEventRouting";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ROUTES from "routes";
import { useUI } from "state";
import { ScreenPage } from "templates";
import { forgotPassword } from "lib/loginApi";
import openToast from "utils/openToast";
import { z } from "zod";

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const { navigateWithEvent } = useEventRouting();
  const { colorScheme } = useUI();
  const EVENT_ASSETS = useAppAssetsWithSrc();

  const schema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .trim()
          .min(1, t("forms.validation.required"))
          .email(t("forms.validation.email")),
      }),
    [t],
  );

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { email: "" },
  });

  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = form;

  const onSubmit = async ({ email }: z.infer<typeof schema>) => {
    try {
      const api = await forgotPassword(email.trim());
      // Contract M2: surface the response message verbatim (identical for
      // every input — never branch on it). i18n copy is the fallback; note
      // the BE message is EN-only, flagged with BE for AR.
      openToast(
        api.data?.message || t("auth.forgotPassword.genericSuccess"),
        "success",
      );
      // replace: a Back press must not re-open the form — re-submitting would
      // regenerate the password and invalidate the one already emailed.
      navigateWithEvent(ROUTES.login, { replace: true });
    } catch (error) {
      // FetchError is promise-like: it resolves to the processed error for the
      // statuses it handles, and to undefined for anything else (network
      // failure, unhandled status). Stay on the screen either way so the user
      // can resubmit — the contract's 422 is explicitly retryable, and it says
      // nothing about whether the address exists.
      const resolved = (await Promise.resolve(error).catch(() => undefined)) as
        | { error?: string }
        | undefined;
      openToast(
        resolved?.error || t("auth.forgotPassword.genericError"),
        "error",
      );
    }
  };

  return (
    <ScreenPage
      desktopBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.src
          : EVENT_ASSETS.login_BG_desktopDark.src
      }
      desktopBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.fallbackSrc
          : EVENT_ASSETS.login_BG_desktopDark.fallbackSrc
      }
      mobileBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.src
          : EVENT_ASSETS.login_BG_mobileDark.src
      }
      mobileBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.fallbackSrc
          : EVENT_ASSETS.login_BG_mobileDark.fallbackSrc
      }
    >
      <h2 className="font-bold mb-4 text-2xl md:text-4xl -text--theme-colors-text-title">
        {t("auth.forgotPassword.title")}
      </h2>
      <p className="-text--theme-colors-text-body">
        {t("auth.forgotPassword.subtitle")}
      </p>
      <p className="mt-2 text-sm -text--theme-colors-text-body">
        {t("auth.forgotPassword.invalidatesOldPassword")}
      </p>
      <Form {...form}>
        <Stack
          component="form"
          className="mt-16"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormField
            name="email"
            // Legacy Input is not forwardRef-compatible — drop RHF's ref.
            render={({ field: { ref: _ref, ...field } }) => (
              <FormItem>
                <FormLabel isRequired>{t("common.labels.email")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder={t("auth.forgotPassword.emailPlaceholder")}
                  />
                </FormControl>
                <FormMessage className="min-h-4" />
              </FormItem>
            )}
          />
          <Stack className="mt-8">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={!isValid || isSubmitting}
              isLoading={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              <div className="flex justify-center items-center gap-x-s10">
                <Text>{t("auth.forgotPassword.submitButton")}</Text>
                <FontAwesomeIcon name="fa-chevron-right" />
              </div>
            </Button>
          </Stack>
          <Stack className="mt-4 items-center">
            <Button
              type="button"
              variant="link"
              onClick={() => navigateWithEvent(ROUTES.login)}
            >
              {t("auth.forgotPassword.backToLogin")}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </ScreenPage>
  );
};

export default ForgotPasswordPage;
